import React from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import SearchIcon from "@mui/icons-material/Search";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import DownloadIcon from "@mui/icons-material/Download";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import SchoolIcon from "@mui/icons-material/School";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonIcon from "@mui/icons-material/Person";
import PersonAvatar from "../home/schedule_components/PersonAvatar";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { objectToCSV, downloadCSV } from "../../../utilities/csvExport";
import { formatName } from "../../../helpers/helpers";

const TRACK_SORT_OPTIONS = [
  { value: "completion_desc", label: "Completion (high to low)" },
  { value: "completion_asc", label: "Completion (low to high)" },
  { value: "name", label: "Track name (A–Z)" },
  { value: "assigned_desc", label: "Most assigned" },
];

function TrackProgressByTrackDisplay(props) {
  const [searchInput, setSearchInput] = React.useState("");
  const [selectedTrackUuid, setSelectedTrackUuid] = React.useState(null);
  const [sortBy, setSortBy] = React.useState("completion_desc");
  const [sortMenuAnchor, setSortMenuAnchor] = React.useState(null);

  const trackSummaries = React.useMemo(() => {
    const tracksMap = new Map();

    (props.storeDetailTrainingDataByEmployee || []).forEach((employee) => {
      const employeeName = formatName(
        [employee.first_name, employee.last_name].filter(Boolean).join(" ")
      );

      (employee?.user?.learning_track_enrollments || []).forEach((enrollment) => {
        const trackUuid = enrollment?.track?.uuid;
        if (!trackUuid) return;

        if (!tracksMap.has(trackUuid)) {
          tracksMap.set(trackUuid, {
            trackUuid,
            trackName: enrollment?.track?.name || "Untitled Track",
            assignedCount: 0,
            completedCount: 0,
            totalSectionsCompleted: 0,
            totalSections: 0,
            employees: [],
          });
        }

        const summary = tracksMap.get(trackUuid);
        const sectionsCompletedCount = enrollment?.sections_completed_count || 0;
        const totalSectionsCount = enrollment?.total_sections_count || 0;
        const rawProgressPct =
          totalSectionsCount > 0
            ? Math.round((sectionsCompletedCount / totalSectionsCount) * 100)
            : enrollment?.completed_at
              ? 100
              : 0;
        const progressPct = Math.max(0, Math.min(100, rawProgressPct));
        const isCompleted = Boolean(enrollment?.completed_at) || progressPct >= 100;

        summary.assignedCount += 1;
        summary.completedCount += isCompleted ? 1 : 0;
        summary.totalSectionsCompleted += sectionsCompletedCount;
        summary.totalSections += totalSectionsCount;
        summary.employees.push({
          employeeId: employee.id,
          employeeName,
          sectionsCompletedCount,
          totalSectionsCount,
          progressPct,
          isCompleted,
          isBlocked: Boolean(enrollment?.is_blocked),
          completedAt: enrollment?.completed_at,
          effectiveDueDate: enrollment?.effective_due_date,
        });
      });
    });

    return Array.from(tracksMap.values())
      .map((track) => ({
        ...track,
        completionPct:
          track.assignedCount > 0
            ? Math.round((track.completedCount / track.assignedCount) * 100)
            : 0,
      }))
      .sort((a, b) => {
        if (b.completionPct !== a.completionPct) {
          return b.completionPct - a.completionPct;
        }
        return a.trackName.localeCompare(b.trackName);
      });
  }, [props.storeDetailTrainingDataByEmployee]);

  const filteredTrackSummaries = React.useMemo(() => {
    const normalizedSearch = searchInput.trim().toLowerCase();
    const filtered = normalizedSearch
      ? trackSummaries.filter((track) =>
          track.trackName.toLowerCase().includes(normalizedSearch)
        )
      : trackSummaries;
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "completion_asc":
          return (
            a.completionPct - b.completionPct ||
            a.trackName.localeCompare(b.trackName)
          );
        case "name":
          return a.trackName.localeCompare(b.trackName);
        case "assigned_desc":
          return (
            b.assignedCount - a.assignedCount ||
            a.trackName.localeCompare(b.trackName)
          );
        case "completion_desc":
        default:
          return (
            b.completionPct - a.completionPct ||
            a.trackName.localeCompare(b.trackName)
          );
      }
    });
  }, [searchInput, trackSummaries, sortBy]);

  const selectedTrack = React.useMemo(
    () =>
      trackSummaries.find((track) => track.trackUuid === selectedTrackUuid) ||
      null,
    [trackSummaries, selectedTrackUuid]
  );

  React.useEffect(() => {
    if (!selectedTrackUuid) return;
    if (!trackSummaries.some((track) => track.trackUuid === selectedTrackUuid)) {
      setSelectedTrackUuid(null);
    }
  }, [selectedTrackUuid, trackSummaries]);

  // Report the drilled-into track up to the parent so the header breadcrumb can
  // show "By Track › Server Certification". Clears on deselect and on unmount.
  React.useEffect(() => {
    if (!props.onDetailChange) return undefined;
    if (selectedTrack) {
      props.onDetailChange({
        label: selectedTrack.trackName,
        onClear: () => setSelectedTrackUuid(null),
      });
    } else {
      props.onDetailChange(null);
    }
    return () => {
      if (props.onDetailChange) props.onDetailChange(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrack]);

  function getEmployeeStatus(employee) {
    if (employee.isBlocked) return "Blocked";
    if (employee.isCompleted) return "Completed";
    if (employee.sectionsCompletedCount > 0) return "In Progress";
    return "Assigned";
  }

  function getStatusClassNames(status) {
    if (status === "Completed") {
      return "bg-emerald-100 text-emerald-700";
    }
    if (status === "In Progress") {
      return "bg-violet-100 text-violet-700";
    }
    if (status === "Blocked") {
      return "bg-amber-100 text-amber-700";
    }
    return "bg-slate-100 text-slate-700";
  }

  function formatTimestamp(timestamp) {
    if (!timestamp) return "—";
    return new Date(timestamp).toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function formatDueDate(timestamp) {
    if (!timestamp) return "—";
    return new Date(timestamp).toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  }

  function isOverdue(employee) {
    if (!employee.effectiveDueDate || employee.isCompleted) return false;
    const now = new Date();
    const todayUTC = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate()
    );
    return employee.effectiveDueDate < todayUTC;
  }

  const sortedSelectedTrackEmployees = React.useMemo(() => {
    if (!selectedTrack) return [];

    const statusOrder = {
      Blocked: 0,
      Assigned: 1,
      "In Progress": 2,
      Completed: 3,
    };

    return [...selectedTrack.employees].sort((a, b) => {
      const statusA = getEmployeeStatus(a);
      const statusB = getEmployeeStatus(b);

      if (statusOrder[statusA] !== statusOrder[statusB]) {
        return statusOrder[statusA] - statusOrder[statusB];
      }
      if (b.progressPct !== a.progressPct) {
        return b.progressPct - a.progressPct;
      }
      return a.employeeName.localeCompare(b.employeeName);
    });
  }, [selectedTrack]);

  const exportTrackSummaryCSV = () => {
    const rows = filteredTrackSummaries.map((track) => ({
      "Track Name": track.trackName,
      Completed: track.completedCount,
      Assigned: track.assignedCount,
      "Completion %": `${track.completionPct}%`,
      "Sections Completed": track.totalSectionsCompleted,
      "Total Sections": track.totalSections,
    }));

    if (rows.length === 0) return;
    const csv = objectToCSV(rows);
    const storeName = props.selectedStore?.store_name || "store";
    downloadCSV(csv, `track_progress_${storeName.replace(/\s+/g, "_")}.csv`);
  };

  const exportSelectedTrackCSV = () => {
    if (!selectedTrack) return;

    const rows = sortedSelectedTrackEmployees.map((employee) => ({
      "Track Name": selectedTrack.trackName,
      "Team Member": employee.employeeName,
      Status: getEmployeeStatus(employee),
      "Sections Completed": employee.sectionsCompletedCount,
      "Total Sections": employee.totalSectionsCount,
      "Completion %": `${employee.progressPct}%`,
      "Due Date": employee.effectiveDueDate
        ? new Date(employee.effectiveDueDate).toLocaleDateString("en-US", {
            timeZone: "UTC",
          })
        : "",
      "Completed At": employee.completedAt
        ? new Date(employee.completedAt).toLocaleString("en-US")
        : "",
    }));

    if (rows.length === 0) return;
    const csv = objectToCSV(rows);
    downloadCSV(
      csv,
      `${selectedTrack.trackName.replace(/\s+/g, "_")}_status.csv`
    );
  };

  if (trackSummaries.length === 0) {
    return (
      <div className="px-6 py-8">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <SchoolIcon
            className="text-slate-300 mx-auto mb-4"
            style={{ fontSize: "48px" }}
          />
          <p className="text-slate-600">No tracks assigned in this location.</p>
          <p className="text-slate-400 text-sm mt-2">
            Track progress will appear here once employees are assigned to tracks.
          </p>
        </div>
      </div>
    );
  }

  if (selectedTrack) {
    const blockedCount = selectedTrack.employees.filter((employee) => employee.isBlocked).length;

    return (
      <div className="px-6 py-8">
        <div className="mb-6">
          <button
            onClick={() => setSelectedTrackUuid(null)}
            className="flex items-center gap-2 text-violet-600 hover:text-violet-700 text-sm font-medium mb-4 transition-colors"
          >
            <ChevronLeftIcon className="-ml-2" style={{ fontSize: "24px" }} />
            Back to Track Progress
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-1">
                {selectedTrack.trackName}
              </h2>
              <p className="text-sm text-slate-600">
                Team member completion status for this track
              </p>
            </div>
            <button
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
              onClick={exportSelectedTrackCSV}
            >
              <DownloadIcon style={{ fontSize: "20px" }} />
              <span className="font-medium">Export CSV</span>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
          <div className="grid grid-cols-3 gap-6">
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                Assigned
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {selectedTrack.assignedCount}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                Completed
              </div>
              <div className="text-2xl font-bold text-emerald-600">
                {selectedTrack.completedCount}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                Completion Rate
              </div>
              <div className="text-2xl font-bold text-violet-700">
                {selectedTrack.completionPct}%
              </div>
            </div>
          </div>
          {blockedCount > 0 && (
            <div className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-flex">
              {blockedCount} {blockedCount === 1 ? "person is" : "people are"} blocked
            </div>
          )}
        </div>

        <div className="-mx-6 border-y border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Team Member
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Completion Progress
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Completed At
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {sortedSelectedTrackEmployees.map((employee) => {
                  const status = getEmployeeStatus(employee);
                  return (
                    <tr key={employee.employeeId} className="hover:bg-slate-50 transition-colors duration-150">
                      <td className="align-middle px-6 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="mr-3 shrink-0">
                            <PersonAvatar
                              view="daily"
                              name={employee.employeeName}
                              bgColor={
                                props.employeeAvatarColorById?.[
                                  employee.employeeId
                                ] || "bg-slate-600"
                              }
                            />
                          </div>
                          <div className="text-xs font-medium text-slate-900">
                            {employee.employeeName}
                          </div>
                        </div>
                      </td>
                      <td className="align-middle px-6 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatusClassNames(
                            status
                          )}`}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="align-middle px-6 py-3">
                        <div className="space-y-2 min-w-[220px]">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">
                              {employee.sectionsCompletedCount} / {employee.totalSectionsCount}{" "}
                              sections
                            </span>
                            <span className="text-sm font-medium text-slate-900">
                              {employee.progressPct}%
                            </span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-violet-500 to-violet-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${employee.progressPct}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="align-middle px-6 py-3 whitespace-nowrap">
                        {employee.effectiveDueDate ? (
                          <div
                            className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${
                              isOverdue(employee)
                                ? "bg-red-50 border border-red-200 text-red-700"
                                : "bg-amber-50 border border-amber-200 text-amber-700"
                            }`}
                          >
                            <CalendarTodayIcon style={{ fontSize: "14px" }} />
                            <span>
                              {isOverdue(employee) ? "Overdue " : "Due "}
                              {formatDueDate(employee.effectiveDueDate)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                      <td className="align-middle px-6 py-3 whitespace-nowrap">
                        {employee.completedAt ? (
                          <div className="flex items-center gap-2 text-sm text-emerald-700">
                            <CheckCircleIcon style={{ fontSize: "18px" }} />
                            <span>{formatTimestamp(employee.completedAt)}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={(e) => setSortMenuAnchor(e.currentTarget)}
            className={`flex h-[46px] items-center gap-2 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium transition-colors hover:border-slate-300 hover:bg-slate-50 ${
              sortBy !== "completion_desc" ? "text-violet-700" : "text-slate-800"
            }`}
          >
            <SwapVertIcon style={{ fontSize: "18px" }} />
            Sort
            <KeyboardArrowDownIcon
              className="text-slate-500"
              style={{ fontSize: "18px" }}
            />
          </button>
          <div className="w-[280px]">
            <div className="relative">
              <SearchIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 transform text-slate-400"
                style={{ fontSize: "20px" }}
              />
              <input
                className="h-[46px] w-full rounded-lg border border-slate-200 pl-10 pr-4 text-sm outline-none transition-all duration-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                type="text"
                placeholder="Search tracks..."
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </div>
          </div>
        </div>
        <button
          className="flex h-[46px] items-center gap-2 whitespace-nowrap rounded-lg bg-emerald-600 px-5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700"
          onClick={exportTrackSummaryCSV}
        >
          <DownloadIcon style={{ fontSize: "18px" }} />
          Export CSV
        </button>
      </div>

      {/* Sort menu */}
      <Menu
        anchorEl={sortMenuAnchor}
        open={Boolean(sortMenuAnchor)}
        onClose={() => setSortMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 240,
            borderRadius: "12px",
            boxShadow: "0 10px 30px -12px rgba(0,0,0,0.25)",
            border: "1px solid #e2e8f0",
          },
        }}
      >
        <div className="px-4 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
          Sort by
        </div>
        {TRACK_SORT_OPTIONS.map((option) => {
          const active = sortBy === option.value;
          return (
            <MenuItem
              key={option.value}
              onClick={() => {
                setSortBy(option.value);
                setSortMenuAnchor(null);
              }}
              sx={{
                fontSize: 14,
                color: active ? "#6d28d9" : "#334155",
                py: 1,
              }}
            >
              <ListItemIcon sx={{ minWidth: 30 }}>
                {active && <CheckIcon sx={{ fontSize: 18, color: "#6d28d9" }} />}
              </ListItemIcon>
              {option.label}
            </MenuItem>
          );
        })}
      </Menu>

      <div className="-mx-6 border-y border-slate-200 bg-white">
        {filteredTrackSummaries.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Track
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Assigned Team Members
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Completion Progress
                  </th>
                  <th className="relative px-6 py-4">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredTrackSummaries.map((track) => (
                  <tr
                    key={track.trackUuid}
                    onClick={() => setSelectedTrackUuid(track.trackUuid)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors duration-150"
                  >
                    <td className="align-middle px-6 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                          <SchoolIcon
                            className="text-violet-600"
                            style={{ fontSize: "16px" }}
                          />
                        </div>
                        <div className="text-xs font-semibold text-slate-900">
                          {track.trackName}
                        </div>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-slate-700">
                        <PersonIcon style={{ fontSize: "16px" }} />
                        <span className="text-xs font-medium">
                          {track.assignedCount}
                        </span>
                        <span className="text-xs text-slate-500">
                          assigned
                        </span>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3">
                      <div className="space-y-2 min-w-[260px]">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs text-slate-600">
                            <AssignmentIcon style={{ fontSize: "16px" }} />
                            <span>
                              {track.completedCount} / {track.assignedCount} completed
                            </span>
                          </div>
                          <span className="text-xs font-medium text-slate-900">
                            {track.completionPct}%
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-violet-500 to-violet-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${track.completionPct}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end">
                        <ChevronRightIcon className="text-slate-400 transition-colors" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <InfoOutlinedIcon
              className="text-slate-300 mx-auto mb-4"
              style={{ fontSize: "48px" }}
            />
            <p className="text-slate-600 font-medium mb-2">
              No tracks found
            </p>
            <p className="text-slate-400 text-sm">
              Try a different search term to find a track.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default TrackProgressByTrackDisplay;
